import { useMemo, useState } from 'react';

import {
  JadeGlassPanel,
  PrimaryActionButton,
  SecondaryActionButton,
  StatePanel,
  StonePanel,
} from '@aztec-realms/ui';

import {
  type TradeResource,
  type TribeExchangeOrder,
  useTribeExchangeCommand,
  useTribeExchangeQuery,
} from './data/tribe-exchange-api';
import { commandId } from './data/use-vertical-slice';

const resourceLabels: Readonly<Record<TradeResource, string>> = {
  maize: 'Maize',
  stone: 'Stone',
  timber: 'Timber',
};
const referenceWeights: Readonly<Record<TradeResource, number>> = {
  maize: 1,
  stone: 2,
  timber: 2,
};
const resources: readonly TradeResource[] = ['maize', 'stone', 'timber'];

interface DraftOffer {
  offeredResourceCode: TradeResource;
  offeredAmount: number;
  requestedResourceCode: TradeResource;
  requestedAmount: number;
}

interface Confirmation {
  action: 'create' | 'fill' | 'cancel';
  order?: TribeExchangeOrder;
  draft?: DraftOffer;
}

function timeRemaining(expiresAt: string): string {
  const milliseconds = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const hours = Math.max(1, Math.ceil(milliseconds / 3_600_000));
  return `${hours}h remaining`;
}

function OfferTerms({ order, own = false }: { order: TribeExchangeOrder; own?: boolean }) {
  return (
    <dl className="tribe-exchange-terms">
      <div>
        <dt>{own ? 'Escrowed' : 'You receive'}</dt>
        <dd>
          {order.offeredAmount} {resourceLabels[order.offeredResourceCode]}
        </dd>
      </div>
      <span aria-hidden="true">⇄</span>
      <div>
        <dt>{own ? 'You request' : 'You give'}</dt>
        <dd>
          {order.requestedAmount} {resourceLabels[order.requestedResourceCode]}
        </dd>
      </div>
    </dl>
  );
}

export function TribeExchangePanel({
  tribeId,
  locked,
  safetyLocked,
}: {
  tribeId: string;
  locked: boolean;
  safetyLocked: boolean;
}) {
  const board = useTribeExchangeQuery(tribeId);
  const marketCommand = useTribeExchangeCommand(tribeId);
  const [offeredResourceCode, setOfferedResourceCode] = useState<TradeResource>('maize');
  const [offeredAmount, setOfferedAmount] = useState(100);
  const [requestedResourceCode, setRequestedResourceCode] = useState<TradeResource>('stone');
  const [requestedAmount, setRequestedAmount] = useState(50);
  const [confirmation, setConfirmation] = useState<Confirmation>();
  const requestedRange = useMemo(() => {
    const offeredValue = Math.max(1, offeredAmount) * referenceWeights[offeredResourceCode];
    const requestWeight = referenceWeights[requestedResourceCode];
    return {
      minimum: Math.max(1, Math.ceil((offeredValue * 0.8) / requestWeight)),
      maximum: Math.max(1, Math.floor((offeredValue * 1.25) / requestWeight)),
    };
  }, [offeredAmount, offeredResourceCode, requestedResourceCode]);

  const submitConfirmation = () => {
    if (!confirmation) return;
    if (confirmation.action === 'create' && confirmation.draft) {
      marketCommand.mutate({
        type: 'create',
        commandId: commandId(`market.create.${tribeId}`),
        ...confirmation.draft,
      });
    } else if (
      confirmation.order &&
      (confirmation.action === 'fill' || confirmation.action === 'cancel')
    ) {
      marketCommand.mutate({
        type: confirmation.action,
        commandId: commandId(`market.${confirmation.action}.${confirmation.order.id}`),
        orderId: confirmation.order.id,
      });
    }
    setConfirmation(undefined);
  };

  return (
    <section aria-labelledby="tribe-exchange-title" className="tribe-exchange">
      <header className="tribe-exchange__heading">
        <div>
          <span className="realm-screen__eyebrow">Member exchange · escrow protected</span>
          <h2 id="tribe-exchange-title">Caravan Exchange</h2>
          <p>Offer gathered supplies directly to another member. No jade, knowledge or credits.</p>
        </div>
        <span className="tribe-exchange__seal" aria-label="Trade is server settled">
          Balanced ledger
        </span>
      </header>

      {confirmation && (
        <JadeGlassPanel
          aria-label="Confirm exchange"
          className="tribe-exchange-confirm"
          ornament={2}
          role="dialog"
        >
          <span className="realm-screen__eyebrow">Confirm exchange</span>
          {confirmation.action === 'create' && confirmation.draft ? (
            <>
              <h3>Place this offer for the Tribe?</h3>
              <p>
                You give{' '}
                <strong>
                  {confirmation.draft.offeredAmount}{' '}
                  {resourceLabels[confirmation.draft.offeredResourceCode]}
                </strong>{' '}
                from your balance now. You receive{' '}
                <strong>
                  {confirmation.draft.requestedAmount}{' '}
                  {resourceLabels[confirmation.draft.requestedResourceCode]}
                </strong>{' '}
                only when another member accepts.
              </p>
              <small>The offered resources remain in server escrow for up to 24 hours.</small>
            </>
          ) : confirmation.order ? (
            <>
              <h3>
                {confirmation.action === 'cancel'
                  ? 'Cancel and return your resources?'
                  : `Exchange with ${confirmation.order.sellerDisplayName}?`}
              </h3>
              {confirmation.action === 'cancel' ? (
                <p>
                  {confirmation.order.offeredAmount}{' '}
                  {resourceLabels[confirmation.order.offeredResourceCode]} returns from escrow.
                </p>
              ) : (
                <OfferTerms order={confirmation.order} />
              )}
              <small>This is one all-or-nothing server-settled exchange.</small>
            </>
          ) : null}
          <div className="tribe-exchange-confirm__actions">
            <PrimaryActionButton
              disabled={locked || marketCommand.isPending}
              loading={marketCommand.isPending}
              onClick={submitConfirmation}
            >
              {confirmation.action === 'cancel' ? 'Return resources' : 'Confirm exchange'}
            </PrimaryActionButton>
            <SecondaryActionButton onClick={() => setConfirmation(undefined)}>
              Go back
            </SecondaryActionButton>
          </div>
        </JadeGlassPanel>
      )}

      <div className="tribe-exchange__layout">
        <StonePanel className="tribe-exchange-create" ornament={1}>
          <h3>Make an offer</h3>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setConfirmation({
                action: 'create',
                draft: {
                  offeredResourceCode,
                  offeredAmount,
                  requestedResourceCode,
                  requestedAmount,
                },
              });
            }}
          >
            <fieldset>
              <legend>You give</legend>
              <label>
                <span>Resource</span>
                <select
                  onChange={(event) => {
                    const next = event.target.value as TradeResource;
                    setOfferedResourceCode(next);
                    if (next === requestedResourceCode) {
                      setRequestedResourceCode(resources.find((item) => item !== next) ?? 'stone');
                    }
                  }}
                  value={offeredResourceCode}
                >
                  {resources.map((resource) => (
                    <option key={resource} value={resource}>
                      {resourceLabels[resource]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Amount</span>
                <input
                  inputMode="numeric"
                  max={5000}
                  min={1}
                  onChange={(event) => setOfferedAmount(event.target.valueAsNumber)}
                  required
                  type="number"
                  value={offeredAmount}
                />
              </label>
            </fieldset>
            <fieldset>
              <legend>You receive</legend>
              <label>
                <span>Resource</span>
                <select
                  onChange={(event) => {
                    const next = event.target.value as TradeResource;
                    setRequestedResourceCode(next);
                    if (next === offeredResourceCode) {
                      setOfferedResourceCode(resources.find((item) => item !== next) ?? 'maize');
                    }
                  }}
                  value={requestedResourceCode}
                >
                  {resources.map((resource) => (
                    <option key={resource} value={resource}>
                      {resourceLabels[resource]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Amount</span>
                <input
                  aria-describedby="trade-value-range"
                  inputMode="numeric"
                  max={requestedRange.maximum}
                  min={requestedRange.minimum}
                  onChange={(event) => setRequestedAmount(event.target.valueAsNumber)}
                  required
                  type="number"
                  value={requestedAmount}
                />
              </label>
            </fieldset>
            <small id="trade-value-range">
              Protected range: {requestedRange.minimum}–{requestedRange.maximum}{' '}
              {resourceLabels[requestedResourceCode].toLowerCase()}.
            </small>
            <PrimaryActionButton disabled={locked || Boolean(confirmation)} type="submit">
              Review offer
            </PrimaryActionButton>
          </form>
        </StonePanel>

        <div className="tribe-exchange-board">
          <header>
            <h3>Open offers</h3>
            <small>{board.data?.orders.length ?? 0} available</small>
          </header>
          {board.isPending && (
            <StatePanel kind="loading" title="Opening the caravan ledger">
              Reading current member offers from the realm server.
            </StatePanel>
          )}
          {board.error && (
            <StatePanel
              actionLabel="Try again"
              kind="error"
              onAction={() => void board.refetch()}
              title="The Exchange could not open"
            >
              {board.error.message}
            </StatePanel>
          )}
          {board.data?.orders.map((order) => (
            <JadeGlassPanel className="tribe-exchange-offer" key={order.id} ornament={1}>
              <header>
                <div>
                  <strong>{order.isOwn ? 'Your offer' : order.sellerDisplayName}</strong>
                  <small>{timeRemaining(order.expiresAt)}</small>
                </div>
                <span>{order.isOwn ? 'Escrowed' : 'Member offer'}</span>
              </header>
              <OfferTerms order={order} own={order.isOwn} />
              {order.isOwn ? (
                <SecondaryActionButton
                  disabled={locked || Boolean(confirmation)}
                  onClick={() => setConfirmation({ action: 'cancel', order })}
                >
                  Cancel offer
                </SecondaryActionButton>
              ) : (
                <PrimaryActionButton
                  disabled={locked || Boolean(confirmation)}
                  onClick={() => setConfirmation({ action: 'fill', order })}
                >
                  Review exchange
                </PrimaryActionButton>
              )}
            </JadeGlassPanel>
          ))}
          {board.data?.orders.length === 0 && (
            <StatePanel kind="empty" title="No open offers yet">
              Place the first balanced offer. Another active member can accept it.
            </StatePanel>
          )}
        </div>
      </div>
      {safetyLocked && (
        <p className="inline-error" role="status">
          Exchange actions unlock after you stop safely. Reviewing offers remains available.
        </p>
      )}
      {marketCommand.error && (
        <p className="inline-error" role="alert">
          {marketCommand.error.message}
        </p>
      )}
      {marketCommand.isSuccess && (
        <p className="tribe-exchange__success" role="status">
          The server ledger accepted the exchange action.
        </p>
      )}
    </section>
  );
}
